import React from 'react';
import { fromPairs, map, find, head, merge, get, isEmpty, isEqual, isUndefined } from 'lodash';
import classnames from 'classnames';
import { withRouter } from 'react-router-dom';

// local dependencies
import { withNetworkState } from 'ui/hoc/withNetworkState';
import { Form } from 'modules/form';
import { button as Button } from 'modules/button';
import { getLabel } from 'utils/getLabel';
import { Typeahead } from 'modules/typeahead';
import { MatchCriteria } from 'modules/matchCriteria';
import { widgetPreloader as WidgetPreloader } from 'ui/views/generic/widgetPreloader';
import { isInbound } from 'resolvers/callStateEntities';
import { AuthFlowCallerIdentificationPharmacySearchWidget } from 'ui/containers/AuthFlowCallerIdentificationPharmacySearchWidget';
import { dimensions } from 'config/dimensions/pharmacy-auth-criteria-grid';
import { inputField as InputField } from 'ui/views/generic/inputField';
import * as ROUTE_PATHS from 'constants/route-paths';
import { BLANK_VALUE_SYMBOL, DATA_TYPES, GLOBAL_LABELS } from 'constants/common';
import { radioList as RadioList } from 'ui/views/generic/radioList';

// caller detail section (editable)
const CallerDetailSectionEditable = ( props ) => {

	const callerDetails = props.componentData.sections.callerDetails;
	const fieldsData = callerDetails.fields;

	// extracting actions
	const cancelAction = find( callerDetails.actions, { name: 'cancel' } );
	const saveAction = find( callerDetails.actions, { name: 'save' } );

	// extracting item Data
	const itemData = merge( head( props.componentData.items ) );

	return (
		<div className='ui-auth-flow-caller-identification-pharmacy__caller-details'>
			<div className='ui-auth-flow-caller-identification-pharmacy__caller-details__title'>
				{ getLabel( callerDetails.title ) }
			</div>

			<Form
				fields={ fromPairs( map( fieldsData, ( field ) => [ field.name, field ] ) ) } // convert to object as specified by `Form` prop
				values={ itemData } // default values
				isValid={ false }
				mode={ props.mode }
				onSubmit={ ( { values, isValid } ) => {
					if ( isValid ) {
						props.onFormSubmit( {
							memberId: props.editItemId,
							title: values.title,
							firstName: values.firstName,
							lastName: values.lastName,
							clinicNumber: values.clinicNumber,
						} );
					}
				} }
			>
				{ ( { fields, values, isValid, errors, onChange, onSumbit } = {} ) => {
					const classNameField = classnames( {
						'ui-auth-flow-caller-identification-pharmacy__caller-details__detailsForm__fields-inputEditable': ! isEqual( props.mode, 'readonly' ),
						'ui-auth-flow-caller-identification-pharmacy__caller-details__detailsForm__fields-inputReadonly': isEqual( props.mode, 'readonly' )
					} );

					return (
						<form
							onSubmit={ onSumbit }
							className='ui-auth-flow-caller-identification-pharmacy__caller-details__detailsForm'
						>
							{ /* fields */ }
							{ map( fields, ( field ) => {
								return (
									<field.view
										key={ field.name }
										className={ classNameField }
										name={ field.name }
										required={ field.required }
										placeholder={ field.placeholder }
										label={ field.title }
										onChange={ onChange }
										value={ values[ field.name ] }
										error={ errors[ field.name ] }
										mode={ props.mode }
									/>
								);
							} ) }

							{ /* submit/edit action */ }
							<Button
								id={ saveAction.name }
								disabled={ !isValid }
								type='submit'
								size='large'
								label={ saveAction.title }
								className='ui-auth-flow-caller-identification-pharmacy__caller-details__detailsForm__fields-button'
							/>

							{/* cancel action */}
							{ isEmpty( props.componentData.items ) || isEqual( props.mode, 'readonly' ) ? null : (
								<Button
									id={ cancelAction.name }
									size='large'
									outline={ true }
									title={ cancelAction.title }
									className='ui-auth-flow-caller-identification-pharmacy__caller-details__detailsForm__fields-button'
									onClick={ () => props.onToggleEditMode( false ) }
								/>
							) }
						</form>
					);
				} }
			</Form>
		</div>
	);
};

// set display name
CallerDetailSectionEditable.displayName = 'authFlowCallerIdentificationPharmacy.CallerDetailSectionEditable';

/* ------------------ */

// caller detail section (readonly)
const CallerDetailSectionReadOnly = ( props ) => {

	const callerDetails = props.componentData.sections.callerDetails;
	const fieldsData = callerDetails.fields;

	// extracting actions
	const editAction = find( callerDetails.actions, { name: 'edit' } );

	// extracting item Data
	const itemData = merge( head( props.componentData.items ) );

	return (
		<div className='ui-auth-flow-caller-identification-pharmacy__caller-details'>
			<div className='ui-auth-flow-caller-identification-pharmacy__caller-details__title'>
				{ getLabel( callerDetails.title ) }
			</div>
			
			<div className='ui-auth-flow-caller-identification-pharmacy__caller-details__readonly-view'>
				{ /* fields */ }
				{ map( fieldsData, ( field ) => {
					return (
						<InputField
							key={ field.name }
							className='ui-auth-flow-caller-identification-pharmacy__caller-details__readonly-view__field'
							name={ field.name }
							label={ field.title }
							value={ itemData[ field.name ] }
							mode='readonly'
						/>
					);
				} ) }

				{/* edit action */}
				<Button
					size='large'
					title={ editAction.title }
					onClick={ () => {
						props.onToggleEditMode( true, get( itemData, 'memberId', null ), 'edit' );
					} }
				/>
			</div>
		</div>
	);
};

// set display name
CallerDetailSectionReadOnly.displayName = 'authFlowCallerIdentificationPharmacy.CallerDetailSectionReadOnly';

/* ------------------ */

// pharmacy search section
const PharmacySearch = ( props ) => {

	// extracting caller identification details and its fields
	const callerIdentification = get( props, 'componentData.sections.callerIdentification', {} );
	const callerIdentificationFields = get( callerIdentification, 'fields', [] );
	
	// get search type field
	const searchTypeField = find( callerIdentificationFields, { type: DATA_TYPES.RADIO_LIST } );
	const searchTypeOptions = get( props, 'componentData.options.pharmacySearchTypes', [] );

	return (
		<div className='ui-auth-flow-caller-identification-pharmacy__pharmacy-search'>
			{
				map( callerIdentificationFields, ( field ) => {
					if( DATA_TYPES.RADIO_LIST === field.type ) {
						return (
							<div
								key={ field.name }
								className='ui-auth-flow-caller-identification-pharmacy__pharmacy-search__field'
							>
								<RadioList
									options={ searchTypeOptions }
									value={ props.pharmacySearchType }
									onChange={ props.onPharmacySearchTypeChange }
								/>
							</div>
						);
					} else if( ( DATA_TYPES.TEXT === field.type ) && ! props.isPharmacyVerificationNeeded ) {

						const searchType = find( searchTypeOptions, { value: props.pharmacySearchType } );

						const placeholder = ! isUndefined( searchType ) ? getLabel( searchType.title ) : BLANK_VALUE_SYMBOL;

						return (
							<div
								key={ field.name }
								className='ui-auth-flow-caller-identification-pharmacy__pharmacy-search__field'
							>
								<InputField
									className='field-value-search__typeahead'
									size='large'
									placeholder={ isEmpty( props.pharmacySearchType ) ? { refId: GLOBAL_LABELS.DISPLAY_TEXT_TYPE_TO_SEARCH, value: '' } : {
										refId: '',
										value: getLabel( field.placeholder, { searchType: placeholder } ),
									} }
									disabled={ searchTypeField ? isEmpty( props.pharmacySearchType ) : false }
									showLabel={ false }
									value={ props.clinicNumber }
									onChange={ props.updateClinicNumber }
								/>
							</div>
						);
					} else if( DATA_TYPES.SEARCH === field.type && props.isPharmacyVerificationNeeded ) {

						// request payload
						const requestPayload = ! searchTypeField ? {} : {
							data: {
								[ searchTypeField.name ]: props.pharmacySearchType
							}
						};

						return (
							<div
								key={ field.name }
								className='ui-auth-flow-caller-identification-pharmacy__pharmacy-search__field'
							>
								<Typeahead
									className='field-value-search__typeahead'
									size='large'
									url={ get( field, 'url' ) }
									placeholder={ isEmpty( props.pharmacySearchType ) ? { refId: GLOBAL_LABELS.DISPLAY_TEXT_TYPE_TO_SEARCH, value: '' } : {
										refId: '',
										value: getLabel( field.placeholder, { searchType: props.pharmacySearchType } ),
									} }
									disabled={ searchTypeField ? isEmpty( props.pharmacySearchType ) : false }
									onQueryChange={ props.onPharmacySearchQueryChange }
									requestPayload={ requestPayload }
									onChange={ ( { selected } ) => {
										props.onSelectPharmacySearch( head( selected ) ); // update selected value
									} }
								/>
							</div>
						);
					} else {
						return null;
					}
				} )
			}
		</div>
	);
};

// set display name
PharmacySearch.displayName = 'authFlowCallerIdentificationPharmacy.PharmacySearch';

/* ------------------- */

// pharmacy match criteria section
const PharmacyCriteria = ( props ) => {

	// extract Item Data
	const itemData = head( props.componentData.items );

	// extract actions for next button
	const actions = get( props.dataForMatchCriteria, 'actions' );
	const action = !isInbound() ? find( actions, { name: 'nextOutbound' } ) : find( actions, { name: 'nextInbound' } );
	log.debug( itemData );

	return (
				<div className='ui-auth-flow-caller-identification-pharmacy__callerVerification'>
					{ /* Match Criteria Div */ }
					<div className='ui-auth-flow-caller-identification-pharmacy__callerVerification__matchCriteria'>
						<MatchCriteria
							onMarkVerified={ props.handleMarkVerifiedAndShowNext }
							data={ props.dataForMatchCriteria }
							saveCriteriaSelection={ props.saveCriteriaSelection }
							layout={ dimensions }
						/>
					</div>
					
					{ /* the partition */ }
					<div className='ui-auth-flow-caller-identification-pharmacy__callerVerification__partition'></div>

					{ /* the next button */ }
					<Button
						className='ui-auth-flow-caller-identification-pharmacy__callerVerification__action'
						size='large'
						title={ action.title }
						disabled={ ! props.isMemberVerified }
						postIconId='arrow'
						showPostIcon={true}
						iconWidth='20'
						iconHeight='30'
						onClick={ () => props.handleActionClick( {
							item: itemData,
							value: props.actionsForNext,
						} ) }
					/>
				</div>
	);
};

// set display name
PharmacyCriteria.displayName = 'authFlowCallerIdentificationPharmacy.PharmacyCriteria';

/************************************************/

// next button  for clinic number
const ClinicNumberNextButton = ( props ) => {

	// extract Item Data
	const itemData = head( props.componentData.items );

	// extract actions for next button
	const actions = get( props.componentData, 'sections.callerVerification.actions' );
	const action = !isInbound() ? find( actions, { name: 'nextOutbound' } ) : find( actions, { name: 'nextInbound' } );

	return (
				<div className='ui-auth-flow-caller-identification-pharmacy__callerVerification'>
					{ /* the next button */ }
					<Button
						className='ui-auth-flow-caller-identification-pharmacy__callerVerification__actionClinicNumber'
						size='large'
						title={ action.title }
						disabled={ ! props.isMemberVerified }
						postIconId='arrow'
						showPostIcon={true}
						iconWidth='20'
						iconHeight='30'
						onClick={ () => props.handleActionClick( {
							item: itemData,
							value: props.actionsForNext,
						} ) }
					/>
				</div>
	);
};

// set display name
PharmacyCriteria.displayName = 'authFlowCallerIdentificationPharmacy.ClinicNumberNextButton';

/************************************************/

// Success View
export const authFlowCallerIdentificationPharmacySuccess = ( props ) => {

    // dynamic class name
    const widgetClassName = classnames( 'ui-auth-flow-caller-identification-pharmacy', props.className );

	// if call has not started, display the pharmacy search widget
	if( ! props.hasCallStarted ) {
		return (
			<div className={ widgetClassName }>
				<AuthFlowCallerIdentificationPharmacySearchWidget
					widget={ props.componentData.sections.callerSearch }
					callerType={ props.callerType }
				/>

				{/* cancle button shows when there is no call going on */}
				{ isEmpty( props.displayBtnCncl ) ? null : (
                    <div className='ui-auth-flow-caller-identification-pharmacy__navigation'>
                        {
							<Button
								key={ props.displayBtnCncl.name }
								title={ props.displayBtnCncl.title }
								size='large'
								outline={ true }
								onClick={ () => props.history.replace( ROUTE_PATHS.DASHBOARD ) }
							/>
                        }
                    </div>
                )
            }
			
			</div>
		);
	} else {
		return (
			<div className={ widgetClassName }>
	 
				{ /* Caller Details form */ }
				{
					isEqual( props.mode, 'readonly' ) ? (
						<CallerDetailSectionReadOnly { ...props } />
					) : (
						<CallerDetailSectionEditable { ...props } />
					)
				}
	
				{ /* Search NPI */ }
				{ ! props.isSearchPharmacyVisible ? null : <PharmacySearch { ...props } /> }
				
				{ /* Match Criteria along with next button */ }
				{ ! props.isCriteriaVisible || !props.isPharmacyVerificationNeeded ? null : <PharmacyCriteria { ...props } /> }

				{ /* next button for clinic number */ }
				{ props.isPharmacyVerificationNeeded ? null : <ClinicNumberNextButton { ...props } /> }

			</div>
		);
	}
};

// set display name
authFlowCallerIdentificationPharmacySuccess.displayName = 'authFlowCallerIdentificationPharmacy';

// set default props
authFlowCallerIdentificationPharmacySuccess.defaultProps = {};

// prop types
authFlowCallerIdentificationPharmacySuccess.propTypes = {};

/* ******************************************************* */

// preloader view
export const authFlowCallerIdentificationPharmacyPreloader = () => {
	return (
		<div className='ui-auth-flow-caller-identification-pharmacy-preloader'>
			<WidgetPreloader />
		</div>
	);
 };

// set display name
authFlowCallerIdentificationPharmacyPreloader.displayName = 'authFlowCallerIdentificationPharmacy';

// set default props
authFlowCallerIdentificationPharmacyPreloader.defaultProps = {};

// prop types
authFlowCallerIdentificationPharmacyPreloader.propTypes = {};

/* ********************************************************* */

/**
 * @type { object }
 * @desc A React presentation/dumb view component.
 * @example
 * import { authFlowCallerIdentificationPharmacy as AuthFlowCallerIdentificationPharmacy } from 'ui/views/widgets/authFlowCallerIdentificationPharmacy';
 *
 * // render
 * <AuthFlowCallerIdentificationPharmacy propName='propValue'/>
 */

export const authFlowCallerIdentificationPharmacy = withNetworkState(
	authFlowCallerIdentificationPharmacyPreloader,
	withRouter( authFlowCallerIdentificationPharmacySuccess ),
);
